import moment from 'moment'
import { CANCELLED_STATUSES } from './flightNerveConstants'
import { mapCanonicalStatusToLegacyLegs } from './flightNerveMapper'
import {
  fetchFlightNerveRoute,
  fetchFlightNerveSchedules,
  fetchFlightNerveStatus,
  recordFlightNerveNote,
} from './flightNerveClient'
import {
  dayIsNonOperating,
  isScheduleCorrectionEnabled,
  isScheduleFirstEnabled,
  planLookupFromSchedules,
  resolveScheduleTimeOverride,
  scheduleFirstWindowDays,
  scheduleWindowDays,
} from './flightNerveSchedules'
import {
  addressMatchesFlightAirportInfo,
  canonicalFlightKey,
  isSameAirportCode,
  localCalendarDate,
} from './flightNervePure'

const normalizeIataCode = value => {
  const code = typeof value === 'string' ? value.trim().toUpperCase() : ''
  return /^[A-Z]{3}$/.test(code) ? code : null
}

// Pull the booked airport's IATA out of a free-text address.
//
// This was a bare /\b([A-Z]{3})\b/ over the whole string, which takes the FIRST
// three-capital token — and in an address that is very often not an airport at all:
// "12 Market Street, San Francisco, California, USA" yielded USA, "…, Dubai, UAE" yields
// UAE, and an all-caps line yields whatever three-letter word comes first (DES, AND, VIA).
//
// A wrong hint is worse than no hint. These feed contradictsBookedAirports and the
// backend's leg selection, so a bogus code can reject the correct leg of a perfectly good
// flight — the "flight does not match your itinerary" class of complaint. Returning null
// just falls back to the depap chain, which is how this worked before hints existed.
//
// Three layers, most trustworthy first:
//   1. the value IS a code — callers hold the airport in both shapes
//   2. a parenthesised code, which is what the app's own airport picker produces:
//      "London Heathrow Airport (LHR), Hounslow, UK"
//   3. a whole segment that is nothing but a code: "… - DXB - Dubai - UAE"
// The TRAILING segment is never considered: that is where the country sits, which is
// exactly how USA and UAE were getting in. A code buried in prose is no longer taken —
// it cannot be told apart from an ordinary word.
const IATA_SEGMENT_SEPARATOR = /\s+[-–—]\s+|,/
export const extractIataCode = value => {
  const text = String(value || '').trim()
  if (!text) return null
  if (/^[A-Z]{3}$/.test(text)) return text

  const parenthesised = text.match(/\(\s*([A-Z]{3})\s*\)/)
  if (parenthesised) return parenthesised[1]

  const segments = text.split(IATA_SEGMENT_SEPARATOR)
  if (segments.length < 2) return null
  return (
    segments
      .slice(0, -1)
      .map(segment => segment.trim())
      .find(segment => /^[A-Z]{3}$/.test(segment)) || null
  )
}

const routeStops = routeInfo =>
  routeInfo && Array.isArray(routeInfo.stops) ? routeInfo.stops : []

// NOTE: there was a module-level store here (setUnresolvedFlightReason /
// getUnresolvedFlightReason) that held a per-flight explanation for the SCREEN — "AA4588
// doesn't operate from DCA on this date — it flies LGA to CHS" in place of a bare "No
// flight found". Removed: the on-screen wording is deliberately left alone, so it never
// had a consumer. The same diagnosis rides in the Slack Reason line instead — see the
// recordFlightNerveNote calls below and ERROR_CODES.md in rolzo-api. Diagnose in the log,
// not in the UI.

// Subsidiary AOC -> parent designator, tried only after the airline's own designator
// comes back empty, and only accepted if the result matches the booked airports.
// See fetchStatusForDate for the full reasoning and the evidence.
//
// Keyed on the ORIGINATING code the user typed, not the post-ICAO_TO_IATA one. Keying on
// the mapped code would over-reach: TWO ICAO codes map to EC — the new EJU (easyJet
// Europe) and the pre-existing EXY (South African Express) — so an EXY lookup would
// silently inherit easyJet's fallback and could surface a U2 flight of the same number.
const PARENT_AIRLINE_ALIAS_BY_ICAO = {
  EJU: 'U2', // easyJet Europe -> easyJet
}
// Someone can also type the IATA form directly, where there is no 3-letter origin to key
// on. Only consulted when no ICAO origin was present, so EXY can never reach it.
const PARENT_AIRLINE_ALIAS_BY_IATA = {
  EC: 'U2',
}

const parentAirlineAliasFor = (originCode, mappedCode) => {
  const origin = String(originCode || '')
    .trim()
    .toUpperCase()
  if (origin) return PARENT_AIRLINE_ALIAS_BY_ICAO[origin] || null
  const mapped = String(mappedCode || '')
    .trim()
    .toUpperCase()
  return PARENT_AIRLINE_ALIAS_BY_IATA[mapped] || null
}

// Match a /route stop against the booked location TEXT. Callers describe the booked
// airport very differently: the VIP surfaces pass a bare IATA ("JFK"), while the
// transfer / add-on-VIP surfaces (AirportPickupSection, AirportDropOffSection) pass a
// full postal address — "John F. Kennedy International Airport, Queens, NY 11430, USA"
// — which may contain NO IATA code at all. So match on code, then airport NAME, then
// CITY, reusing the same tolerant matcher the consumer applies to the resolved flight.
const stopMatchesLocation = (stop, locationText) => {
  if (!stop || !locationText) return false
  if (
    addressMatchesFlightAirportInfo(locationText, {
      iata_faa: stop.iata,
      name: stop.name,
    })
  ) {
    return true
  }
  return addressMatchesFlightAirportInfo(locationText, { name: stop.city })
}

const findStopIndexForLocation = (stops, locationText) =>
  stops.findIndex(stop => stopMatchesLocation(stop, locationText))

// /airline's `depap` names the DEPARTURE airport of the leg we want, so the two
// directions need opposite treatment against the stop chain (SQ26 -> SIN, FRA, JFK):
//  - ARRIVAL  (booked airport is where the leg LANDS)      -> depap = the stop BEFORE it
//  - DEPARTURE (booked airport is where the leg TAKES OFF) -> depap = that stop itself
// The old behaviour — always the flight's canonical origin — selects the FIRST leg,
// which for a multi-stop flight number is the wrong one (SQ26 gave SIN->FRA for a JFK
// arrival, so the airport check failed with "does not match selected airport").
// Returns the depap to query with plus the leg-selection hints we can now state as
// real IATA codes, whatever shape the caller's location text was in.
const resolveLegFromRouteChain = (
  routeInfo,
  { arrivalText, departureText }
) => {
  const stops = routeStops(routeInfo)

  // `depapConfirmed` says whether the stop chain actually PROVED this depap against the
  // booked airport, or whether we fell back to the flight's canonical origin because the
  // chain could not confirm anything. That distinction decides whether a 400 is allowed
  // to be retried without depap — see canRetryWithoutDepap below.
  if (arrivalText) {
    const arrivalIndex = findStopIndexForLocation(stops, arrivalText)
    if (arrivalIndex > 0) {
      const precedingStop = stops[arrivalIndex - 1]
      if (precedingStop && precedingStop.iata) {
        return {
          depap: precedingStop.iata,
          expectedArrivalAirport: stops[arrivalIndex].iata || null,
          depapConfirmed: true,
        }
      }
    }
    // No depap rather than a guessed one. We used to fall back to /route's canonical
    // origin here, which is only what FlightNerve has historically OBSERVED, and it is
    // wrong in both of the ways that matter:
    //   - stale filed route: AF7305 is listed SXB->ORY but operates NCE->CDG, so
    //     `depap=SXB` 400s while the same call without it returns the flight
    //   - multi-leg: the canonical origin selects the FIRST leg, which is the SQ26
    //     SIN->FRA-for-a-JFK-arrival bug
    // And it buys nothing. depap's only unique value is billing — FlightNerve charges
    // per leg for a depap-less MULTI-leg request — but a guess we cannot confirm is as
    // likely to cost us a 400 plus a retry (2 credits) as to save one. Leg selection is
    // handled independently by expectedArrival/expectedDeparture, which the backend's
    // selectPrimaryInstance applies to whatever comes back.
    return {
      depap: null,
      expectedArrivalAirport: null,
      depapConfirmed: false,
    }
  }

  if (departureText) {
    const departureIndex = findStopIndexForLocation(stops, departureText)
    if (departureIndex >= 0 && stops[departureIndex].iata) {
      return {
        depap: stops[departureIndex].iata,
        expectedDepartureAirport: stops[departureIndex].iata,
        depapConfirmed: true,
      }
    }
  }

  // Same reasoning as above: unconfirmed means no depap at all.
  return { depap: null, depapConfirmed: false }
}

// For the airport backfill below: on a multi-stop flight the leg we resolved may sit
// in the MIDDLE of the chain, so routeInfo.departure/arrival (the chain's endpoints)
// are not that leg's airports. Prefer the matching entry in `stops` when we know the
// code we're filling in, and only fall back to the endpoint.
const findRouteStop = (routeInfo, iata) => {
  if (!routeInfo || !iata || !Array.isArray(routeInfo.stops)) return null
  // Equivalence, not equality — /route may list BSL where the booking says EAP.
  return (
    routeInfo.stops.find(stop => stop && isSameAirportCode(stop.iata, iata)) ||
    null
  )
}

export const resolveFlightNerveInfo = async ({
  fname,
  fnumber,
  fOrigName,
  apiDate,
  pickUpAddress,
  dropOffAddress,
  pickUpAirportIata,
  dropOffAirportIata,
  pickUpDate,
  isPickUpContext,
  // The booking's OWN direction, when the caller knows it ('arrival' | 'departure').
  // Authoritative over which address prop happened to carry the airport — see below.
  bookingDirection,
  callerManagesDate,
  shouldRetryPrevDayOnCancelled,
  // The caller's AbortSignal. Threaded all the way to fetch() so a superseded lookup is
  // genuinely cancelled — until now the AbortController in FlightTracking.jsx aborted
  // nothing and every replaced lookup ran its full ladder to completion, which is what
  // put 3-5 calls for one flight number in the same second on FlightNerve's dashboard.
  signal,
}) => {
  // Never start a lookup for an incomplete flight number. The search can fire while the
  // user is still typing ("AF", "A"), which produced `num=` — a request FlightNerve
  // rejects outright, since `num` is required and digits-only. Two wasted calls each time
  // (the dated one plus the cross-midnight retry), for input we already know is not a
  // flight. Bail before any network happens; the caller renders its normal
  // "flight not found" for a null return.
  // The airline code must also carry a LETTER: every real code does (4Y, B6, 3Z, ENT), so
  // an all-digit one means the input had no airline at all ("7305" parses to name=73).
  const airlineCode = String(fname || '')
  if (
    !/^\d+$/.test(String(fnumber || '')) ||
    !/^[A-Z0-9]{2,3}$/i.test(airlineCode) ||
    !/[A-Z]/i.test(airlineCode)
  ) {
    return null
  }

  // Identifies THIS lookup's notes in the shared failure buffer, so a concurrently
  // running leg (VIP transit renders both; an add-on form has both fields) cannot drain
  // them into its own Slack entry. Rebuilt from the parsed parts through the same
  // canonicaliser the reporter uses, so both sides agree on what "this flight" is.
  const flightTag = canonicalFlightKey(`${fOrigName || fname}${fnumber}`)

  // The booked airport, by direction: on an ARRIVAL/pick-up the flight LANDS there
  // (pickUpAddress); on a DEPARTURE/drop-off it TAKES OFF from there (dropOffAddress).
  // These become leg-selection hints on flightNerve/status so a multi-leg response
  // resolves to the leg that actually touches the booked airport; a wrong or absent
  // hint is ignored downstream, never harmful.
  // Prefer the caller's own IATA field over a code scraped from the address text: the
  // transfer / chauffeur search resolves it from the place lookup
  // (searchValues.pickUpLocation.iata / dropOffLocation.iata), so it is authoritative,
  // whereas a postal address may carry no code at all or an unrelated 3-capital token.
  let expectedDepartureAirport =
    normalizeIataCode(dropOffAirportIata) || extractIataCode(dropOffAddress)
  let expectedArrivalAirport =
    normalizeIataCode(pickUpAirportIata) || extractIataCode(pickUpAddress)

  // ── The stated direction wins over which PROP carried the airport ──────────────
  // Everything above infers the booked side from WHICH prop was populated: an airport in
  // dropOff* means the flight departs from it, an airport in pickUp* means it lands there.
  // That inference is only as good as the caller, and it is silently fatal when wrong —
  // a departure booking whose airport arrives as `pickUpAddress` becomes an ARRIVAL hint,
  // the leg it names is then checked against the flight's DESTINATION, and a correct
  // flight is rejected as a mismatch. Observed in production on AI186 (YVR->DEL booked as
  // a departure out of YVR), AF3/AF1/AA42 (JFK->CDG out of JFK), UX1048 and BR31 — every
  // one a real flight the provider returned, every one reported as "Flight not found".
  //
  // The mis-wiring is easy to reintroduce because standalone VIP stores its airport in
  // `pickUpLocation` for BOTH directions (see BookingDetails/Details/index.js), so any
  // caller reading the booking generically hands us a pick-up-shaped value for a
  // departure. Rather than depend on every surface getting the prop right forever, let a
  // caller that knows the booking's direction just SAY so, and place the airport itself.
  //
  // Prefer the prop that matches the direction, so a correctly-wired caller is completely
  // unaffected; fall back to the other prop, which is what repairs a mis-wired one. Only
  // ever runs when the caller passes a direction — everyone else keeps today's inference.
  if (bookingDirection === 'departure' || bookingDirection === 'arrival') {
    const fromDropOff =
      normalizeIataCode(dropOffAirportIata) || extractIataCode(dropOffAddress)
    const fromPickUp =
      normalizeIataCode(pickUpAirportIata) || extractIataCode(pickUpAddress)
    const bookedAirport =
      bookingDirection === 'departure'
        ? fromDropOff || fromPickUp
        : fromPickUp || fromDropOff
    if (bookedAirport) {
      // Set the booked side, and clear the opposite side ONLY when it holds the same
      // airport — that duplicate is the inverted-wiring artifact this reconciliation
      // exists to undo. A genuinely DIFFERENT airport on the other side is real
      // information and must survive: the backend's route board needs both ends
      // (canUseRouteLookup returns false if either is missing), and that third-line
      // /trackbyroute lookup is what rescues flights /airline cannot find on a date —
      // IB379 and IB380 on 30 Nov were both recoverable only that way. Nulling it
      // wholesale would have disabled the rescue for every caller that knows both
      // airports, silently and only on the failure path where it matters.
      const opposite =
        bookingDirection === 'departure'
          ? expectedArrivalAirport
          : expectedDepartureAirport
      const oppositeIsDuplicate =
        opposite && isSameAirportCode(opposite, bookedAirport)
      if (bookingDirection === 'departure') {
        expectedDepartureAirport = bookedAirport
        if (oppositeIsDuplicate) expectedArrivalAirport = null
      } else {
        expectedArrivalAirport = bookedAirport
        if (oppositeIsDuplicate) expectedDepartureAirport = null
      }
    }
  }

  // ── Schedules-first planning (flag-gated) ─────────────────────────────────
  // Ask WHICH day this flight operates before asking /airline about a day we guessed.
  // Non-operating days are free, so a flight number that does not exist costs nothing and
  // never reaches /airline — and that is the bulk of what #flight-tracking-errors reports.
  // A trustworthy projection also names the departure date that LANDS on the booked date,
  // which is the cross-midnight question answered instead of retried, and the leg's own
  // `from`, which is a confirmed depap without paying for /route.
  //
  // Only ever SHORTCUTS. An unusable projection returns 'unknown' and everything below
  // runs exactly as it does today, so the flag can be turned off at any point with no
  // behavioural residue.
  let schedulePlan = null
  if (isScheduleFirstEnabled()) {
    const bookedAirport = isPickUpContext
      ? expectedArrivalAirport
      : expectedDepartureAirport
    const bookedDate = moment(apiDate, 'YYYYMMDD', true).isValid()
      ? moment(apiDate, 'YYYYMMDD').format('YYYY-MM-DD')
      : null
    if (bookedAirport && bookedDate) {
      // An arrival may have departed the previous day, so start the window a day early;
      // a departure cannot, so ask for the one day. Every OPERATING day is a credit.
      const windowDays = scheduleFirstWindowDays(Boolean(isPickUpContext))
      const startDate = isPickUpContext
        ? moment(bookedDate, 'YYYY-MM-DD')
            .subtract(1, 'day')
            .format('YYYYMMDD')
        : apiDate
      const payload = await fetchFlightNerveSchedules({
        flightTag,
        num: fnumber,
        name: fname,
        date: startDate,
        days: windowDays,
        signal,
      })
      schedulePlan = planLookupFromSchedules({
        payload,
        bookedAirport,
        bookedDate,
        isArrival: Boolean(isPickUpContext),
        matchesBookedAirport: isSameAirportCode,
      })
      if (schedulePlan.outcome === 'no-service') {
        // Believed only on a projection built from real observation — see
        // projectionIsTrustworthy. Nothing further is spent.
        recordFlightNerveNote('schedules', 'no_service', 200, flightTag)
        return null
      }
    }
  }

  // A bare IATA lifted straight out of a drop-off address is already the leg's
  // departure airport, so it can be used as depap with no extra call (the VIP
  // departure surfaces hit this). Otherwise walk the route chain, which also
  // upgrades an address-only caller to real IATA hints.
  let depap = expectedDepartureAirport
  let routeInfo = null
  // The caller's own drop-off IATA is the airport the CUSTOMER booked, so it is
  // authoritative by definition — treat it as confirmed. Only a depap we inferred from
  // /route can be an unconfirmed guess.
  let depapConfirmed = Boolean(depap)
  // A depap-less status probe fired alongside /route, kept for the dated lookup below.
  let depaplessProbe = null
  // Whether a probe was FIRED, tracked separately from what it returned. The old guard
  // tested the value, so an aborted or failed probe (null) looked like "no probe" and the
  // chain re-issued the byte-identical request — the FN 007 / FN 008 duplicate.
  let depaplessProbeFired = false

  // Apply a schedules-first match. Setting `depap` here is what makes the /route and probe
  // branches below skip themselves — both are gated on `!depap` — so a planned lookup goes
  // straight to a single targeted /airline: right date, right leg, no chain walk, no
  // previous-day retry (the flat-miss retry only fires when nothing resolves, and the plan
  // has already told us this day operates).
  const schedulePlanMatched = Boolean(
    schedulePlan && schedulePlan.outcome === 'match'
  )
  if (schedulePlanMatched) {
    apiDate = schedulePlan.apiDate || apiDate
    depap = schedulePlan.depap || depap
    depapConfirmed = Boolean(depap)
    // The projection also names the far end of the leg, which is a better arrival hint
    // than anything scraped from an address — but never overwrite a code the BOOKING gave
    // us, which is authoritative by definition.
    if (schedulePlan.arrivalAirport && !expectedArrivalAirport) {
      expectedArrivalAirport = schedulePlan.arrivalAirport
    }
  }
  // Fire the probe only when there is already a booked airport to validate it against.
  // fetchStatusForDate refuses to use a probe it cannot check (canValidateProbe below), so
  // without a hint the call is bought and thrown away, and the depap-guided chain runs
  // anyway — one wasted FlightNerve call on every such lookup. `depap` is
  // expectedDepartureAirport, so inside this !depap branch the arrival hint is the only
  // one that can exist.
  //
  // This never costs an extra call: where the probe would have been USED, that path now
  // spends the same two calls serially (/route then the dated lookup) instead of in
  // parallel — slower by one round trip, identical in volume. Where it would have been
  // discarded, the call simply is not made.
  const probeCanBeValidated = Boolean(expectedArrivalAirport)
  if (!depap && (pickUpAddress || dropOffAddress) && !probeCanBeValidated) {
    routeInfo = await fetchFlightNerveRoute({
      flightTag,
      num: fnumber,
      name: fname,
      signal,
    })
    const resolvedLeg = resolveLegFromRouteChain(routeInfo, {
      arrivalText: pickUpAddress,
      departureText: dropOffAddress,
    })
    depap = resolvedLeg.depap
    depapConfirmed = Boolean(resolvedLeg.depapConfirmed)
    if (resolvedLeg.expectedArrivalAirport) {
      expectedArrivalAirport = resolvedLeg.expectedArrivalAirport
    }
    if (resolvedLeg.expectedDepartureAirport) {
      expectedDepartureAirport = resolvedLeg.expectedDepartureAirport
    }
  } else if (!depap && (pickUpAddress || dropOffAddress)) {
    // /route and a depap-less status call do not depend on each other — /route only tells
    // us WHICH leg to ask for, and the depap-less call asks for all of them. Running them
    // back to back cost two serial round trips (~0.8s each per FlightNerve's own docs) on
    // every arrival lookup, which is most of the way through a 1.4s budget before any
    // retry logic even starts. Fired together instead.
    //
    // This is NOT speculative in the usual sense: for a DIRECT flight the depap-less
    // response is the whole answer, so the probe replaces the depap call rather than
    // adding to it. It is only spent in vain when the chain turns out to need a specific
    // leg — and that case was already paying two calls.
    const [route, probe] = await Promise.all([
      fetchFlightNerveRoute({ flightTag, num: fnumber, name: fname, signal }),
      fetchFlightNerveStatus({
        flightTag,
        num: fnumber,
        name: fname,
        date: apiDate,
        expectedArrivalAirport,
        expectedDepartureAirport,
        signal,
      }),
    ])
    routeInfo = route
    depaplessProbe = probe
    depaplessProbeFired = true
    const resolvedLeg = resolveLegFromRouteChain(routeInfo, {
      arrivalText: pickUpAddress,
      departureText: dropOffAddress,
    })
    depap = resolvedLeg.depap
    depapConfirmed = Boolean(resolvedLeg.depapConfirmed)
    // Only ever UPGRADE a hint. A code confirmed by the stop chain is authoritative, but
    // when the chain can't confirm one (direct flights come back with no `stops` at all)
    // the regex-scraped code is still worth sending: FlightNerve also returns the nested
    // form for a flight with several past instances flying DIFFERENT routes (IA123), and
    // the hint is what picks the right one there.
    if (resolvedLeg.expectedArrivalAirport) {
      expectedArrivalAirport = resolvedLeg.expectedArrivalAirport
    }
    if (resolvedLeg.expectedDepartureAirport) {
      expectedDepartureAirport = resolvedLeg.expectedDepartureAirport
    }
  }

  const legHints = { expectedArrivalAirport, expectedDepartureAirport }

  // One dated lookup, with a depap-less second chance. FlightNerve can reject a perfectly
  // valid date with a 400 when `depap` is ALSO supplied, while the same date WITHOUT it
  // returns the flight, which our airport hints can then pick the right leg out of.
  // Two independent reasons that happens, hence two triggers:
  //
  //  1. UNCONFIRMED depap — the stop chain could not match the booked airport, so we fell
  //     back to /route's canonical origin. That origin is only what FlightNerve has
  //     historically OBSERVED, and it goes stale whenever a route is re-based or retimed:
  //     AF7305 is listed SXB->ORY but has moved to NCE->CDG, so a CDG arrival queried
  //     `depap=SXB` and 400'd, while the same call without depap returns NCE->CDG. A
  //     guess we could not confirm must never be trusted enough to suppress the recovery.
  //     This is the common case and it hits DIRECT flights, which have no `stops` at all.
  //  2. MULTI-STOP chain — even a confirmed depap can 400 once that leg's departure slips
  //     into the past (observed on SQ26's SIN->FRA leg); the nested response still carries
  //     the leg we want.
  //
  // Still NOT unconditional: a confirmed depap on a direct flight (the customer's own
  // drop-off airport) gets no retry, so the ordinary "this date has no such flight" path
  // — mistyped flight number, schedule-gap carriers like IA123 — cannot double its dated
  // lookups. That was the 400-storm the backend circuit-breaker had to be added for.
  // depap is now only ever set when CONFIRMED (resolveLegFromRouteChain returns null
  // otherwise), so the old `!depapConfirmed` arm is unreachable — a guessed depap no
  // longer exists to be retried around. What remains is the multi-leg case: even a
  // confirmed depap can 400 once that leg's departure slips into the past (observed on
  // SQ26's SIN->FRA leg), and the nested response still carries the leg we want.
  const canRetryWithoutDepap =
    Boolean(depap) && depapConfirmed && routeStops(routeInfo).length > 1

  const aliasAirlineCode = parentAirlineAliasFor(fOrigName, fname)

  // Dropping depap widens the query to every leg of the chain, so the response may be a
  // leg that has nothing to do with the booking (SQ26 on a past date yields only the
  // already-flown FRA->JFK leg when FRA is the airport we booked). Reject a result that
  // flatly contradicts a known airport so the retry chain keeps looking instead of
  // handing back the wrong flight. Only a PRESENT, different code counts — a null
  // airport is a live in-air instance awaiting the /route backfill below, not a conflict.
  // Equivalence, not equality: a booking at EAP and a flight reported at BSL are the same
  // airport, and calling that a contradiction discarded valid legs. See
  // EQUIVALENT_AIRPORT_CODE_GROUPS in flightNervePure.js.
  // ── Separate "wrong flight" from "right flight, wrong SIDE" ───────────────────
  // A rejection currently reads the same either way, and the two need opposite responses.
  // If the booked airport is on the leg but on the OTHER end — booked as an arrival at an
  // airport the flight DEPARTS from, or vice versa — then the number and the airport are
  // both right and only the direction is wrong. That is either the agent entering a
  // departure flight in an arrival field (their fix: check the field) or the booking's
  // airport reaching us on the wrong side (ours). Those are indistinguishable from here —
  // the props are the only direction signal and a wrong prop looks exactly like a wrong
  // flight — so this NAMES the shape rather than guessing at a correction: silently
  // accepting the leg would show a customer a flight going the other way, which is worse
  // than the error they get today.
  //
  // Paired with the reporter's own Type field (which is derived independently and is
  // correct), the next occurrence settles it: `Type: Departure` + `hint_side_inverted`
  // can only be our side. Log-only — the on-screen wording is untouched.
  let hintInversionNoted = false
  const noteHintInversion = instance => {
    if (hintInversionNoted) return
    const dep = instance.departure && instance.departure.airportCode
    const arr = instance.arrival && instance.arrival.airportCode
    if (!dep || !arr) return
    const invertedArrivalHint =
      expectedArrivalAirport && isSameAirportCode(dep, expectedArrivalAirport)
    const invertedDepartureHint =
      expectedDepartureAirport &&
      isSameAirportCode(arr, expectedDepartureAirport)
    if (!invertedArrivalHint && !invertedDepartureHint) return
    hintInversionNoted = true
    recordFlightNerveNote(
      'status',
      `hint_side_inverted ${dep}->${arr} (booked ${
        invertedArrivalHint
          ? `arr ${expectedArrivalAirport}, flight DEPARTS it`
          : `dep ${expectedDepartureAirport}, flight ARRIVES there`
      })`,
      200,
      flightTag
    )
  }

  const contradictsBookedAirports = instance => {
    if (!instance || instance.noData) return false
    const arrivalCode = instance.arrival && instance.arrival.airportCode
    const departureCode = instance.departure && instance.departure.airportCode
    const contradicts =
      Boolean(
        expectedArrivalAirport &&
          arrivalCode &&
          !isSameAirportCode(arrivalCode, expectedArrivalAirport)
      ) ||
      Boolean(
        expectedDepartureAirport &&
          departureCode &&
          !isSameAirportCode(departureCode, expectedDepartureAirport)
      )
    if (contradicts) noteHintInversion(instance)
    return contradicts
  }

  // `prefetchedPrimary` lets the caller hand in a response it already paid for, when that
  // response is byte-identical to the request this would otherwise make.
  // `prefetchedPrimary === undefined` means nothing was pre-fetched. A pre-fetched NULL is
  // still an answer — the probe was fired and came back empty, aborted, or failed — and
  // re-issuing the byte-identical request cannot turn that into data. Using `||` here
  // treated null as "nothing fetched" and fired the same URL twice, 200ms apart, which is
  // exactly the duplicate FlightNerve was complaining about.
  const resolveWithAirlineCode = async (name, date, prefetchedPrimary) => {
    const primary =
      prefetchedPrimary !== undefined
        ? prefetchedPrimary
        : await fetchFlightNerveStatus({
            flightTag,
            num: fnumber,
            name,
            date,
            depap,
            ...legHints,
            signal,
          })
    if (!canRetryWithoutDepap || (primary && !primary.noData)) return primary
    const widened = await fetchFlightNerveStatus({
      flightTag,
      num: fnumber,
      name,
      date,
      ...legHints,
      signal,
    })
    return contradictsBookedAirports(widened) ? primary : widened
  }

  // The alias is a per-flight fallback, not a per-date one: attempting it inside every
  // dated lookup (primary, previous day, day-shift reconciliation, cancelled retry) would
  // take the worst-case chain from ~11 sequential calls to ~20 for one EC flight, which
  // is the opposite of the call-volume goal. Tried once, on the date actually booked.
  const fetchStatusForDate = async date => {
    // undefined = no probe was consumed for this date. Distinct from a probe that WAS
    // consumed and yielded null — see resolveWithAirlineCode.
    let probeAsPrimary
    // Use the probe fired alongside /route, but only when it stands on its own: a real
    // result that does not contradict either booked airport. Anything ambiguous (nothing
    // found, or a leg that conflicts with the booking) is discarded and the normal
    // depap-guided chain runs exactly as before — the probe can never weaken a result,
    // only skip a round trip when it already has the answer.
    // Consumed once, and only for the date it was fired for.
    if (date === apiDate && depaplessProbeFired) {
      const probe = depaplessProbe
      depaplessProbe = null
      depaplessProbeFired = false
      // Requires at least one booked airport to check against. With both hints null
      // contradictsBookedAirports can only ever return false, so accepting the probe
      // there would hand back whatever leg the backend picked first (validInstances[0])
      // in preference to the depap-targeted one the chain would have resolved — the
      // opposite of the intended priority for a multi-instance flight like IA123.
      const canValidateProbe = Boolean(
        expectedArrivalAirport || expectedDepartureAirport
      )
      if (
        canValidateProbe &&
        probe &&
        !probe.noData &&
        !contradictsBookedAirports(probe)
      ) {
        return probe
      }

      // The probe is depap-less. Now that a guessed depap is never sent, `depap` is null
      // for exactly the lookups the probe is fired on — which makes the request below
      // byte-identical to the one we just paid for. Re-issuing it cannot return anything
      // different; it only shows up on FlightNerve's dashboard as the same call twice.
      // Reuse it as the primary instead. The alias fallback further down still runs, so
      // the EJU/EC recovery is untouched.
      if (!depap) probeAsPrimary = probe
    }

    const result = await resolveWithAirlineCode(fname, date, probeAsPrimary)
    if (!aliasAirlineCode || date !== apiDate || (result && !result.noData)) {
      return result
    }

    // Subsidiary-AOC fallback. Some carriers file under their own designator but have
    // their flight INSTANCES indexed under the parent's: easyJet Europe (EJU/EC) is the
    // known case — /route lists EC4858 and EC4191, yet /airline 400s on both under EC
    // while U2 returns them correctly (NCE->ORY, NAP->NCE).
    //
    // Deliberately a FALLBACK and not a straight EJU->U2 mapping, because the same number
    // under the two designators is not always the same flight: EC's own network lists
    // 7690 as MXP->AMS, but U2 7690 is MAD->LIS. Mapping blind would hand back another
    // airline's flight — the CAI4134 -> Air China failure again. So we try the correct
    // designator first, only fall back on a flat miss, and put the result through the
    // same airport check the depap-less retry uses, so a same-number-different-flight
    // can never be surfaced.
    const aliased = await resolveWithAirlineCode(aliasAirlineCode, date)
    if (!aliased || aliased.noData) return result
    return contradictsBookedAirports(aliased) ? result : aliased
  }

  let canonical = await fetchStatusForDate(apiDate)

  // Previous-day retry on a flat failure to resolve — ARRIVALS ONLY.
  //
  // /airline is keyed by DEPARTURE date. For a pick-up that matters: a passenger landing
  // at 00:30 boarded the previous day. For a DROP-OFF it cannot: the drop-off date IS the
  // departure date, so yesterday is never the answer in any direction.
  //
  // Worse than a wasted call. Nothing below verifies the retry against the booked date, so
  // on a departure lookup for a flight that simply does not operate that day we fetched
  // YESTERDAY's instance, accepted it, and the downstream check then reported "flight
  // departure date and drop-off date don't match" — an error we manufactured ourselves.
  // Suppressed when a schedule plan chose the date: the projection has already said this
  // day operates and this leg lands on the booked date, so walking to another day would
  // contradict it and spend a credit to do so.
  if (
    !schedulePlanMatched &&
    isPickUpContext &&
    (!canonical || canonical.noData)
  ) {
    const prevApiDate = moment(apiDate, 'YYYYMMDD')
      .subtract(1, 'day')
      .format('YYYYMMDD')
    const retryCanonical = await fetchStatusForDate(prevApiDate)
    // Standalone-VIP surfaces (callerManagesDate) skip the dayShift reconciliation below and
    // run their own cross-midnight correction, so an unverified retry is never repaired: it
    // must land on the day asked about. Without this, a corrected re-query for 16 Oct took
    // back the 15 Oct TN102 that lands on the 14th — the same wrong flight that triggered the
    // correction — the hook dismissed it as a stale echo, and the search spun forever.
    // Guess-based callers keep accepting it as before; dayShift fixes their date. A retry
    // with no arrival time cannot be checked either way, so it keeps the old acceptance.
    const retryArrivalDay =
      retryCanonical &&
      retryCanonical.arrival &&
      localCalendarDate(retryCanonical.arrival.actualTime)
    const retryLandsOnAskedDay =
      !callerManagesDate ||
      !retryArrivalDay ||
      retryArrivalDay === moment(apiDate, 'YYYYMMDD').format('YYYY-MM-DD')
    if (retryCanonical && !retryCanonical.noData && retryLandsOnAskedDay) {
      canonical = retryCanonical
    }
  }

  // Next-day retry — the eastbound date-line case the previous-day step cannot reach.
  // A leg can LAND a calendar day before it departs: TN102 AKL->PPT leaves 16:35 on the
  // 11th and lands 22:20 on the 10th (live flightapi.io, Oct 2026). /airline files it under
  // the 11th, while the 10th returns the onward PPT->LAX leg, which the backend rightly
  // rejects for a PPT arrival. Only taken when its arrival lands on the booked day, so it
  // can find the right instance but never accept a neighbouring one.
  if (
    !schedulePlanMatched &&
    isPickUpContext &&
    (!canonical || canonical.noData)
  ) {
    const bookedApiDay = moment(apiDate, 'YYYYMMDD', true)
    const targetDay = callerManagesDate
      ? bookedApiDay.isValid()
        ? bookedApiDay.format('YYYY-MM-DD')
        : null
      : localCalendarDate(pickUpDate) ||
        (bookedApiDay.isValid() ? bookedApiDay.format('YYYY-MM-DD') : null)
    if (targetDay && bookedApiDay.isValid()) {
      const nextApiDate = moment(apiDate, 'YYYYMMDD')
        .add(1, 'day')
        .format('YYYYMMDD')
      const nextCanonical = await fetchStatusForDate(nextApiDate)
      if (
        nextCanonical &&
        !nextCanonical.noData &&
        nextCanonical.arrival &&
        localCalendarDate(nextCanonical.arrival.actualTime) === targetDay
      ) {
        canonical = nextCanonical
      }
    }
  }

  // Last-resort no-date fallback for carriers FlightNerve tracks live and
  // historically but does NOT publish a forward schedule for (e.g. Iraqi Airways
  // IA123): the dated /airline lookups above 404 with "No scheduled flight
  // found", yet a no-date /airline call still returns the active/next instance.
  //
  // IMPORTANT: a no-date call returns the CURRENT active/most-recent instance,
  // which is the right flight ONLY for a booking happening ~now. For a FUTURE
  // date it would return today's flight — the WRONG day — and (its airport
  // matching the booking) surface a spurious date-mismatch with garbled times.
  // FlightNerve simply has no forward schedule for such carriers, so for a future
  // date we leave it unresolved rather than show the wrong day.
  // Same reasoning: an undated call returns the CURRENT instance, which is by definition
  // not the day the plan selected.
  if (!schedulePlanMatched && (!canonical || canonical.noData)) {
    const requestedDay = moment(apiDate, 'YYYYMMDD', true)
    const isFutureDate =
      requestedDay.isValid() && requestedDay.isAfter(moment.utc(), 'day')
    if (!isFutureDate) {
      const undatedCanonical = await fetchFlightNerveStatus({
        flightTag,
        num: fnumber,
        name: fname,
        depap,
        ...legHints,
        signal,
      })
      if (undatedCanonical && !undatedCanonical.noData) {
        canonical = undatedCanonical
      }
    }
  }

  if (!canonical || canonical.noData) {
    // Carry the backend's reason into the error channel before giving up. `noDataReason`
    // is set when FlightNerve returned a flight it has no times for — real flight, no
    // schedule — which is a different message for the agent than "no such flight" and
    // otherwise reaches Slack looking exactly the same. Nothing else changes: an empty
    // result is still an empty result and the card still shows the same thing.
    if (canonical && canonical.noDataReason) {
      recordFlightNerveNote('status', canonical.noDataReason, 200, flightTag)
    }
    // Route context for the DAILY LOG: a bare "Flight not found" cannot be
    // triaged from Slack — "flight doesn't exist" and "real flight, wrong
    // direction/date" (CA135 booked as an ICN departure when it flies PEK->ICN)
    // read identically. /route knows the pair, so record it next to the failure.
    // Costs at most ONE /route call, on failure paths only, and only when the
    // chain didn't already fetch it (departure flows skip /route); cached 30d
    // per number upstream, so repeats are free. Fully fenced: log enrichment
    // must never break or delay the null return the caller expects.
    let routeNoteRecorded = false
    try {
      const bookedCode = expectedDepartureAirport || expectedArrivalAirport
      const bookedSide = expectedDepartureAirport
        ? `booked dep ${expectedDepartureAirport}`
        : expectedArrivalAirport
        ? `booked arr ${expectedArrivalAirport}`
        : null
      if (bookedSide) {
        const r =
          routeInfo ||
          (await fetchFlightNerveRoute({
            flightTag,
            num: fnumber,
            name: fname,
            signal,
          }))
        const routeDep = r && r.departure && r.departure.iata
        const routeArr = r && r.arrival && r.arrival.iata
        if (routeDep && routeArr) {
          // Does the number actually touch the booked airport? That single bit is the
          // whole triage: touching it means the number and airport are right and the
          // failure is about the DATE or a FlightNerve schedule gap (chase FlightNerve);
          // not touching it means the agent typed a number for another route (chase
          // nobody). `flight_exists LGA->DFW (booked arr CDG)` made the reader work that
          // out by eye, and got it wrong whenever a mid-chain stop or an equivalent code
          // was involved — U2 1217 reads `BSL->EDI (booked dep EAP)`, which looks like a
          // mismatch and is the same airport.
          //
          // So compare here, over every stop (UA1439 is IAH->DFW->EWR: a booking at DFW
          // is on the route and neither endpoint says so) and through
          // isSameAirportCode, and state the answer rather than the raw pair.
          // UNION, not either/or. `stops` happens to carry both endpoints on the
          // responses seen so far (UA1439 = IAH,DFW,EWR), but nothing in the contract
          // promises it — and a stops array that omitted an endpoint would make a booking
          // AT that endpoint read as `route_mismatch X->Y (booked dep X)`, accusing the
          // agent of a wrong number while naming their own airport. Keeping the endpoints
          // in costs nothing and removes that whole failure mode.
          const routePoints = [
            routeDep,
            routeArr,
            ...routeStops(r).map(stop => stop && stop.iata),
          ]
          const touchesBookedAirport = routePoints.some(iata =>
            isSameAirportCode(iata, bookedCode)
          )
          recordFlightNerveNote(
            'route',
            `${
              touchesBookedAirport ? 'route_ok' : 'route_mismatch'
            } ${routeDep}->${routeArr} (${bookedSide})`,
            200,
            flightTag
          )
          routeNoteRecorded = true
        }
      }
    } catch (routeContextError) {
      // Logging garnish only — the lookup outcome is already decided.
    }
    // Never return null without leaving a reason. An empty result and a result we
    // rejected ourselves both surface as "Flight not found", and when neither the
    // backend nor /route said anything the Slack line ends at the dash with nothing
    // after it — the entries nobody could classify (BA920, MX1016, AZ1720, DL3794 in
    // the 7-9 Aug batch, all pick-ups at a HOTEL rather than an airport, so there was
    // no booked airport to check and no note explaining that). Costs no API call: it
    // labels what already happened.
    if (!routeNoteRecorded && !(canonical && canonical.noDataReason)) {
      recordFlightNerveNote(
        'resolver',
        expectedDepartureAirport || expectedArrivalAirport
          ? 'no_instances'
          : 'no_booked_airport',
        200,
        flightTag
      )
    }
    return null
  }

  // ── A PROJECTED route is a guess, and a guess must never accuse the booking ──
  //
  // FlightNerve's confirmed schedule horizon is short — around two days. Past it, /airline
  // still answers with `status: "Scheduled"` and real-looking times, but the instance is
  // flagged `scheduleConfidence: "projected"`: FlightNerve built it from the flight
  // NUMBER'S OPERATING HISTORY, not from a published schedule. Measured on AA4588
  // (2026-08-05): 6 Aug came back DCA<->ACK "confirmed", matching Cirium to the minute,
  // while 8 Aug came back LGA<->CHS "projected" — a different city pair entirely, because
  // that number's history is LGA-based (/route says LGA->MSP, and AA 4xxx regional numbers
  // are reused across routes).
  //
  // Believing that guess is what produced "Flight does not match selected airport" on a
  // standalone-VIP departure booked out of DCA: the leg selection worked perfectly, it was
  // simply handed a projection for the wrong route. The airport hint cannot rescue it
  // either — selectPrimaryInstance falls back to instance[0] when NO instance matches, so
  // a projected payload for another city pair arrives looking like a valid flight.
  //
  // So: when the instance is projected AND it contradicts a booked airport, we have no
  // evidence about this flight and must say so, rather than assert the airport is wrong.
  // Deliberately narrow — a projected instance that AGREES with the booked airport is
  // still useful (it is how every booking more than two days out gets its times), and a
  // CONFIRMED contradiction is still a real mismatch and still reported as one.
  if (
    canonical.scheduleConfidence === 'projected' &&
    contradictsBookedAirports(canonical)
  ) {
    // Rides into the Slack suffix so the channel shows what actually happened instead of a
    // bare "not found". The projected route + booked airport are appended so ops can
    // classify at a glance — "(HNL->MSP, booked LGA)" is user error, no investigation
    // needed — instead of every one of these becoming an API-probing session. The three
    // cases this code covers (wrong flight for the airport, cold first-ask projection,
    // wrong weekday) are indistinguishable without it.
    //
    // Safety: the suffix is appended only when BOTH projected codes exist (a live leg
    // can carry a null arrival — a bare code there would mislead), ASCII '->' so the
    // label survives Slack/JSON/log pipelines, and the base code string is unchanged
    // as a prefix — nothing downstream matches on it (verified), and same-lookup
    // repeats still collapse to one "xN" line because the label is deterministic for
    // a given flight + booking.
    const flownDep = canonical.departure && canonical.departure.airportCode
    const flownArr = canonical.arrival && canonical.arrival.airportCode
    // WHICH side was booked, not just the code. `(IST->PMO, booked PMO)` is unreadable —
    // PMO is on the pair, so the line looks like a false rejection until you work out
    // that the booking was a DEPARTURE from PMO and IST->PMO is the inbound leg. Same
    // wording as the /route note above so both halves of a Reason line read alike.
    const bookedAirportForNote = expectedDepartureAirport
      ? `dep ${expectedDepartureAirport}`
      : expectedArrivalAirport
      ? `arr ${expectedArrivalAirport}`
      : null
    const noteSuffix =
      flownDep && flownArr && bookedAirportForNote
        ? ` (${flownDep}->${flownArr}, booked ${bookedAirportForNote})`
        : ''
    recordFlightNerveNote(
      'status',
      `projected_route_mismatch${noteSuffix}`,
      200,
      flightTag
    )
    return null
  }

  // Backfill airport identity from /route when the resolved instance is missing
  // it. A live/In-Air instance from /airline can carry a NULL arrival (the flight
  // hasn't landed, so the arrival airport isn't populated yet) — e.g. IA123
  // mid-flight returns BGW -> (null). Without the arrival airport, the downstream
  // address match against the booking's selected airport (DXB) fails, the card
  // reports "no flight found", and the search re-fires. /route always knows the
  // canonical origin/destination pair, so fill the gaps from it.
  if (routeInfo) {
    // The leg's own endpoints when we know them (a mid-chain leg's airports are NOT
    // the flight's overall origin/destination), else the chain endpoints as before.
    const arrivalSource =
      findRouteStop(routeInfo, expectedArrivalAirport) || routeInfo.arrival
    const departureSource =
      findRouteStop(routeInfo, depap) || routeInfo.departure

    if (canonical.arrival && !canonical.arrival.airportCode && arrivalSource) {
      canonical.arrival.airportCode = arrivalSource.iata || null
      canonical.arrival.airport =
        arrivalSource.name || canonical.arrival.airport || null
      canonical.arrival.airportCity =
        arrivalSource.city || canonical.arrival.airportCity || null
      canonical.arrival.airportCountryCode =
        arrivalSource.country || canonical.arrival.airportCountryCode || null
    }
    if (
      canonical.departure &&
      !canonical.departure.airportCode &&
      departureSource
    ) {
      canonical.departure.airportCode = departureSource.iata || null
      canonical.departure.airport =
        departureSource.name || canonical.departure.airport || null
      canonical.departure.airportCity =
        departureSource.city || canonical.departure.airportCity || null
      canonical.departure.airportCountryCode =
        departureSource.country ||
        canonical.departure.airportCountryCode ||
        null
    }
  }

  // A PROJECTED instance still missing an endpoint airport AFTER the /route
  // backfill is a guess with a hole in it: the null side can never match the
  // booked airport, so the consumer rejects it, the search re-fires, and every
  // cycle burns the full lookup chain (the LA2218 CUZ->null loop; same family
  // as IA123 before the backfill existed). Unresolved once, deterministically,
  // is the only stable answer. Confirmed instances keep the old behaviour —
  // their airports are facts, and a live in-air leg legitimately fills in later.
  if (
    canonical.scheduleConfidence === 'projected' &&
    ((canonical.arrival && !canonical.arrival.airportCode) ||
      (canonical.departure && !canonical.departure.airportCode))
  ) {
    recordFlightNerveNote(
      'status',
      'projected_incomplete_airports',
      200,
      flightTag
    )
    return null
  }

  // ── Arrival-day reconciliation ──────────────────────────────────────────────
  // FlightNerve keys /airline by DEPARTURE date, but a pick-up is booked against the
  // ARRIVAL date, and the two diverge on every flight that crosses midnight. The caller
  // makes a first guess (FlightTracking subtracts a day when the pickup hour is <= 03:00,
  // because a passenger landing in the small hours departed the previous day), but that
  // guess can be wrong in EITHER direction:
  //   departs 28th 23:55, lands 29th 07:05, pickup 29th 07:30 -> guessed 29th, needs 28th
  //   departs 29th 00:40, lands 29th 02:55, pickup 29th 03:00 -> guessed 28th, needs 29th
  // So instead of always retrying the previous day, compare the resolved arrival's LOCAL
  // calendar date against the pickup date and shift in whichever direction the difference
  // points. Local, not UTC: the pickup date is expressed in the arrival airport's terms,
  // and a UTC compare mis-dates every late-evening arrival (23:30 EDT is "tomorrow" UTC).
  // Skipped when the caller manages dates itself (useDateAsIs -> the standalone-VIP
  // surfaces, which run their own CROSS_MIDNIGHT correction). Two corrections fighting
  // over the same lookup would flicker or land on the wrong instance, so reconciliation
  // is only for the guess-based callers: transfer / add-on VIP / chauffeur.
  if (
    isPickUpContext &&
    !callerManagesDate &&
    canonical.arrival &&
    canonical.arrival.actualTime
  ) {
    const pickUpDay = localCalendarDate(pickUpDate)
    const arrivalDay = localCalendarDate(canonical.arrival.actualTime)
    const dayShift =
      pickUpDay && arrivalDay
        ? moment(pickUpDay, 'YYYY-MM-DD').diff(
            moment(arrivalDay, 'YYYY-MM-DD'),
            'days'
          )
        : 0
    // Up to TWO days either way. One is enough for an ordinary cross-midnight hop, but an
    // ultra-long-haul crossing the date line lands two calendar days after it departs:
    // AC19 leaves YVR 22:50 on the 17th and reaches SIN 05:15 on the 19th, so a pickup
    // booked for the 18th needs the instance that departed on the 16th. Capped at two
    // because a wider gap means the wrong flight entirely rather than an off-by-one
    // departure date, and walking further would invent a match.
    //
    // Widening the search is safe on its own because the ACCEPTANCE test below is what
    // actually protects us: a retry is only taken when its arrival lands on the pickup
    // date, so a bigger window can find the right instance but never smuggle in a wrong one.
    if (dayShift !== 0 && Math.abs(dayShift) <= 2) {
      const shiftedApiDate = moment(apiDate, 'YYYYMMDD')
        .add(dayShift, 'day')
        .format('YYYYMMDD')
      const retryCanonical = await fetchStatusForDate(shiftedApiDate)
      // Take the retry only when it actually lands on the pickup date, so one wrong
      // instance can't be swapped for another. When the retry carries no arrival time
      // at all we can't verify either way, so fall back to the old behaviour of
      // preferring it — the original is already known to be the wrong day.
      if (retryCanonical && !retryCanonical.noData) {
        const retryArrivalDay =
          retryCanonical.arrival &&
          localCalendarDate(retryCanonical.arrival.actualTime)
        // A retry with no arrival time cannot be verified. That is tolerated at one day
        // out (the original is already known to be the wrong day, and a live in-air
        // instance legitimately has no arrival yet) but NOT at two: the wider window was
        // opened for date-line crossings, and accepting an unverifiable instance two days
        // from the booking is how you show someone the wrong flight.
        const verified = retryArrivalDay
          ? retryArrivalDay === pickUpDay
          : Math.abs(dayShift) === 1
        if (verified) {
          canonical = retryCanonical
        }
      }
    }
  }

  // Cancelled retry: mirrors retryPreviousDayIfCancelled.
  if (
    shouldRetryPrevDayOnCancelled &&
    CANCELLED_STATUSES.includes(canonical.status || '')
  ) {
    const prevApiDate = moment(apiDate, 'YYYYMMDD')
      .subtract(1, 'day')
      .format('YYYYMMDD')
    const retryCanonical = await fetchStatusForDate(prevApiDate)
    if (
      retryCanonical &&
      !retryCanonical.noData &&
      !CANCELLED_STATUSES.includes(retryCanonical.status || '')
    ) {
      canonical = retryCanonical
    }
  }

  // ── Schedule correction ─────────────────────────────────────────────────────
  // /airline has no forward schedule: for any date it holds no flight record for — every
  // future booking — it returns one fixed time per flight, always with status "Scheduled".
  // Right for flights that operate at the same time daily, wrong for the rest. /schedules
  // has the real per-date times for those, so swap them in when (and only when) the flight
  // demonstrably does not run to a fixed time. Verified against Cirium on 6 flights; see
  // flightNerveSchedules.js for the evidence and why a blanket swap would be wrong.
  //
  // Gated on status "Scheduled": once /airline has a real record (In Air, Arrived,
  // Delayed) its times are actuals and authoritative — never overwrite those.
  // ── Provenance gate ─────────────────────────────────────────────────────────
  // This correction exists for ONE defect, and it is FlightNerve's: its /airline has no
  // forward schedule, so for any date it holds no record for it returns the same fixed time
  // on every date (BA606 came back 14:25 for 21 Aug, 24 Aug AND 27 Sep). /schedules carries
  // the real per-date times for those, which is what gets swapped in below.
  //
  // flightapi.io does not have that defect — it returns date-specific times (BA606: 17:20
  // on 21 Aug, 17:25 on 27 Sep, both matching British Airways). Running the correction on
  // its data would therefore be worse than useless: FlightNerve's /schedules gave 19:31 for
  // that same 27 Sep leg, so the swap would replace a correct primary time with a
  // two-hour-wrong projection — the exact failure moving to flightapi.io was meant to end.
  //
  // Stated as a POSITIVE test so it fails safe: an unrecognised or future provider is left
  // alone rather than corrected. An absent dataSource means an entry cached before
  // provenance existed, which could only have come from FlightNerve.
  //
  // It also takes a ~1s FlightNerve round trip out of the critical path of every
  // future-dated search on the primary path, and the credit with it.
  const correctableProvenance =
    !canonical.dataSource || canonical.dataSource === 'flightNerve'

  if (
    isScheduleCorrectionEnabled() &&
    correctableProvenance &&
    canonical.status === 'Scheduled' &&
    canonical.departure &&
    canonical.arrival
  ) {
    // Hardened two ways, deliberately:
    //  1. Fully fenced in try/catch. The correction is a POLISH step on an already
    //     resolved flight — no /schedules quirk (odd payload shape, missing arrival
    //     times, network failure) may ever throw and take down a resolution that
    //     succeeded. On any error the uncorrected /airline times ship as-is.
    //  2. Calendar-date guard below: an override may adjust TIMES, never move an
    //     endpoint onto a different LOCAL calendar day. The consumers validate the
    //     arrival/departure DATE against the booking and re-fetch on a mismatch
    //     (cross-midnight logic) — an override that shifted a date would make the
    //     validator and the correction fight each other on every re-fetch: an
    //     infinite search loop. A date-moving override is treated as wrong data
    //     and skipped, with a note for the Slack trail.
    try {
      const legDate = localCalendarDate(canonical.departure.actualTime)
      if (legDate) {
        const legApiDate = legDate.split('-').join('')
        const schedulePayload = await fetchFlightNerveSchedules({
          flightTag,
          num: fnumber,
          name: fname,
          date: legApiDate,
          days: scheduleWindowDays(),
          signal,
        })

        // /schedules PROVING this exact date has no service outranks /airline's own
        // "confirmed" flag on it — see dayIsNonOperating for the TK1393 case this guards
        // against: /airline served Monday's timetable for a Tuesday it does not fly, and
        // called it confirmed. Rejected the same way as the schedule-first 'no-service'
        // outcome elsewhere in this file — a null canonical, which the frontend already
        // renders as "flight not found".
        if (dayIsNonOperating(schedulePayload, legApiDate)) {
          recordFlightNerveNote(
            'schedules',
            'day_does_not_operate',
            200,
            flightTag
          )
          return null
        }

        const override = resolveScheduleTimeOverride(
          schedulePayload,
          legApiDate,
          {
            departureAirport: canonical.departure.airportCode,
            arrivalAirport: canonical.arrival.airportCode,
          }
        )
        const keepsCalendarDate = (overrideTime, canonicalTime) => {
          const overrideDay = localCalendarDate(overrideTime)
          const canonicalDay = localCalendarDate(canonicalTime)
          // Unknown on either side -> cannot prove it moved; allow (matches the old
          // behaviour for flights whose canonical times were absent).
          if (!overrideDay || !canonicalDay) return true
          return overrideDay === canonicalDay
        }
        if (
          override &&
          (!keepsCalendarDate(
            override.departure.actualTime,
            canonical.departure.actualTime
          ) ||
            !keepsCalendarDate(
              override.arrival.actualTime,
              canonical.arrival.actualTime
            ))
        ) {
          recordFlightNerveNote(
            'schedules',
            'correction_skipped_date_shift',
            200,
            flightTag
          )
        } else if (override) {
          canonical.departure.scheduledTime = override.departure.scheduledTime
          canonical.departure.actualTime = override.departure.actualTime
          canonical.arrival.scheduledTime = override.arrival.scheduledTime
          canonical.arrival.actualTime = override.arrival.actualTime
          // The old estimate and delay were measured against the placeholder schedule, so
          // they mean nothing now. A flight this far out has no real delay yet anyway.
          canonical.departure.estimatedTime = override.departure.scheduledTime
          canonical.arrival.estimatedTime = override.arrival.scheduledTime
          canonical.departure.delayMinutes = null
          canonical.arrival.delayMinutes = null
        }
      }
    } catch (scheduleCorrectionError) {
      // Note only — the resolved flight ships with its /airline times.
      recordFlightNerveNote('schedules', 'correction_failed', 200, flightTag)
    }
  }

  return mapCanonicalStatusToLegacyLegs(canonical)
}
